import { inject, Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { Router } from "@angular/router";
import {
  CODE_CHALLENGE_METHOD,
  FIRST_LOGIN_PARAMETER_NAME,
  GRANT_TYPE,
  RESPONSE_TYPE,
} from "../constants";
import { KeyModel, TokenResponse } from "../models";
import { environment } from "../../../environments";
import { ApiService } from "./api.service";
import { KeyService } from "./key.service";
import { KeyTypeEnum } from "../enums";
import { generateCodeChallenge, generateCodeVerifier } from "../utils";
import { authEndpointConfig } from "../configs";
import { finalize } from "rxjs";
import { TokenExchangeRequest } from "../models";
import { LanguageStore, UserStore } from "../stores";
import { StorageService } from "@merchant-webapp/components";
import { CONFIRM_SWITCH_BANNER_CLOSED } from "../constants/session-storage-keys.constants";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly userStore = inject(UserStore);
  private readonly languageStore = inject(LanguageStore);

  constructor(
    private readonly httpClient: HttpClient,
    private readonly router: Router,
    private readonly keyService: KeyService,
    private readonly apiService: ApiService,
    private readonly storageService: StorageService,
  ) {}

  public authenticationCheck(): boolean {
    const authorizationCode: string | null =
      this.retrieveAuthorizationCodeFromUrl();
    if (authorizationCode) {
      this.authenticate(authorizationCode);
      return false;
    }
    return true;
  }

  public authenticate(authorizationCode: string | null): void {
    if (authorizationCode) {
      const codeVerifier: KeyModel = this.keyService.getKey(
        KeyTypeEnum.CODE_VERIFIER,
      );
      if (codeVerifier && codeVerifier.value) {
        this.exchangeAuthorizationCode(authorizationCode, codeVerifier.value);
        this.resetSessionStorageKeys();
        return;
      }
    }
    void this.redirectToConnect();
  }

  public logout(): void {
    this.apiService
      .post(null, authEndpointConfig.logoutUrl())
      .pipe(finalize((): void => this.performIdentityLogout()))
      .subscribe();
  }

  private retrieveAuthorizationCodeFromUrl(): string | null {
    const currentUrl: URL = new URL(window.location.href);
    return currentUrl.searchParams.get("code");
  }

  private async redirectToConnect(): Promise<void> {
    const codeVerifier: string = generateCodeVerifier();
    const key: KeyModel = {
      type: KeyTypeEnum.CODE_VERIFIER,
      value: codeVerifier,
    };
    this.keyService.saveOrUpdateKey(key);
    const codeChallenge: string = await generateCodeChallenge(codeVerifier);

    const redirectUrl: URL = new URL(environment.auth.connectAuthorize.url);
    redirectUrl.searchParams.append(
      "scope",
      environment.auth.connectAuthorize.scope.join(" "),
    );
    redirectUrl.searchParams.append("response_type", RESPONSE_TYPE);
    redirectUrl.searchParams.append("client_id", environment.auth.clientId);
    redirectUrl.searchParams.append("code_challenge", codeChallenge);
    redirectUrl.searchParams.append(
      "code_challenge_method",
      CODE_CHALLENGE_METHOD,
    );
    redirectUrl.searchParams.append(
      "redirect_uri",
      environment.auth.redirectUri,
    );
    redirectUrl.searchParams.append(
      "acr_values",
      environment.auth.connectAuthorize.acr_values,
    );
    redirectUrl.searchParams.append(
      "ui_locales",
      this.languageStore.selectedLanguage().name,
    );

    window.location.href = redirectUrl.href;
  }

  private exchangeAuthorizationCode(
    authorizationCode: string,
    codeVerifier: string,
  ): void {
    const headers: HttpHeaders = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
    });

    const payload: URLSearchParams = new URLSearchParams();
    payload.set("grant_type", GRANT_TYPE);
    payload.set("client_id", environment.auth.clientId);
    payload.set("code_verifier", codeVerifier);
    payload.set("code", authorizationCode);
    payload.set("redirect_uri", environment.auth.redirectUri);

    this.httpClient
      .post<TokenResponse>(environment.auth.tokenAuthorize.url, payload, {
        headers: headers,
      })
      .subscribe({
        next: (data: TokenResponse): void => {
          this.keyService.deleteKey(KeyTypeEnum.CODE_VERIFIER);
          this.keyService.saveOrUpdateKey({
            type: KeyTypeEnum.ID_TOKEN,
            value: data.id_token,
          });
          this.exchangeIdentityToken(data.access_token);
        },
      });
  }

  private exchangeIdentityToken(identityToken: string): void {
    const tokenExchangeRequest: TokenExchangeRequest = {
      identity_token: identityToken,
    };
    this.apiService
      .post(tokenExchangeRequest, authEndpointConfig.tokenExchangeUrl())
      .subscribe({
        next: (response: unknown): void => {
          this.navigateToRoot(response as Record<string, any>);
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 404) {
            this.performIdentityLogout();
          }
          throw error;
        },
      });
  }

  private navigateToRoot(params: Record<string, any>): void {
    const { first_login } = params;
    this.storageService.storeData(FIRST_LOGIN_PARAMETER_NAME, first_login);
    void this.router.navigate(["/"]);
  }

  public performIdentityLogout(): void {
    const idTokenKey = this.keyService.getKey(KeyTypeEnum.ID_TOKEN);
    if (idTokenKey) {
      const idToken: string = idTokenKey.value;
      const redirectUrl: URL = new URL(environment.auth.connectLogout.url);
      redirectUrl.searchParams.append("id_token_hint", idToken);
      redirectUrl.searchParams.append(
        "post_logout_redirect_uri",
        environment.auth.redirectUri,
      );

      this.keyService.deleteKey(KeyTypeEnum.ID_TOKEN);
      window.location.href = redirectUrl.href;
    } else {
      this.authenticate(null);
    }
  }

  private resetSessionStorageKeys() {
    sessionStorage.removeItem(CONFIRM_SWITCH_BANNER_CLOSED);
  }
}
